<template>
  <!--
    Single-select typeahead. Type to filter the option list, click to
    pick exactly one. Visually compatible with TypeAheadMultiSelectBox
    so a row of mixed single/multi filters reads consistently, but the
    selection model is a scalar (string/number) instead of an array.

    Emits: update-value(newValue | null)
  -->
  <div class="w-full relative" ref="container">
    <div class="flex items-center h-8 bg-white border border-gray-300">
      <input
        ref="input"
        type="text"
        :value="displayValue"
        @input="onInput"
        @focus="onFocus"
        @keydown.enter.exact.prevent="selectFirstFiltered"
        @keydown.escape.prevent="hideOptions"
        @keydown.down.prevent="showOptions"
        :placeholder="effectivePlaceholder"
        class="flex-1 px-2 h-full bg-transparent text-sm focus:outline-none"
      />
      <button
        v-if="hasSelection"
        type="button"
        @mousedown.prevent="clearSelection"
        title="Clear selection"
        class="px-2 h-full text-gray-500 hover:bg-indigo-100 hover:text-gray-800"
      >
        ×
      </button>
      <button
        type="button"
        @mousedown.prevent="toggleOptions"
        class="px-2 h-full text-gray-700 hover:bg-indigo-100"
      >
        ▾
      </button>
    </div>

    <!--
      Options dropdown. position:absolute so it doesn't disturb grid
      row sizing. max-height keeps long lists scrollable in-place.
    -->
    <div
      v-if="visibleOptions"
      class="absolute z-50 w-full max-h-60 overflow-y-auto bg-white border border-gray-300 shadow-lg"
    >
      <div
        v-if="filteredOptions.length === 0"
        class="px-2 py-1 text-xs text-gray-500"
      >
        No matches
      </div>
      <div
        v-for="option in filteredOptions"
        :key="option.value"
        @mousedown.prevent="selectOption(option)"
        :class="[
          'px-2 py-1 text-sm cursor-pointer hover:bg-indigo-100 truncate',
          { 'bg-indigo-50 font-medium': isSelected(option) },
        ]"
        :title="option.label"
      >
        {{ option.label }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, onMounted, onUnmounted } from "vue";

const props = defineProps({
  // Array of { label: string, value: any }
  options: { type: Array, default: () => [] },
  // Externally-controlled selected value (scalar). null/undefined = no selection.
  defaultValue: { default: null },
  // Placeholder shown when there's no selection AND no search text.
  placeholder: { type: String, default: "" },
  // Override the empty-state placeholder when the user hasn't picked yet.
  noSelectionMessage: { type: String, default: "" },
});
const emit = defineEmits(["update-value"]);

const selectedValue = ref(props.defaultValue);
const searchText = ref("");
const visibleOptions = ref(false);
const focused = ref(false);
const container = ref(null);
const input = ref(null);

// Keep selectedValue in sync with parent-driven reset (e.g. reset filters).
watch(
  () => props.defaultValue,
  (v) => {
    selectedValue.value = v;
  }
);

const hasSelection = computed(
  () => selectedValue.value !== null && selectedValue.value !== undefined && selectedValue.value !== ""
);

const selectedLabel = computed(() => {
  if (!hasSelection.value) return "";
  const found = (props.options || []).find((o) => o.value === selectedValue.value);
  return found ? found.label : String(selectedValue.value);
});

// When the input is focused, show what the user is typing (so they
// can see their query). When unfocused, show the selected label so
// the cell reads as "Company: Acme Inc.".
const displayValue = computed(() => {
  if (focused.value) return searchText.value;
  return selectedLabel.value;
});

const effectivePlaceholder = computed(() => {
  if (hasSelection.value) return "";
  return props.placeholder || props.noSelectionMessage || "";
});

const filteredOptions = computed(() => {
  const q = (focused.value ? searchText.value : "").trim().toLowerCase();
  if (!q) return props.options || [];
  return (props.options || []).filter(
    (o) => o.label && o.label.toLowerCase().includes(q)
  );
});

function isSelected(option) {
  return option.value === selectedValue.value;
}

function onFocus() {
  focused.value = true;
  searchText.value = "";
  visibleOptions.value = true;
}

function onInput(event) {
  searchText.value = event.target.value;
  visibleOptions.value = true;
}

function showOptions() {
  visibleOptions.value = true;
}

function hideOptions() {
  visibleOptions.value = false;
  focused.value = false;
  searchText.value = "";
  if (input.value) input.value.blur();
}

function toggleOptions() {
  if (visibleOptions.value) {
    hideOptions();
  } else {
    visibleOptions.value = true;
    if (input.value) input.value.focus();
  }
}

function selectOption(option) {
  selectedValue.value = option.value;
  emit("update-value", option.value);
  hideOptions();
}

function selectFirstFiltered() {
  if (filteredOptions.value.length > 0) {
    selectOption(filteredOptions.value[0]);
  }
}

function clearSelection() {
  selectedValue.value = null;
  searchText.value = "";
  emit("update-value", null);
  hideOptions();
}

function handleClickOutside(event) {
  if (container.value && !container.value.contains(event.target)) {
    hideOptions();
  }
}

onMounted(() => document.addEventListener("mousedown", handleClickOutside));
onUnmounted(() => document.removeEventListener("mousedown", handleClickOutside));
</script>
